import React from 'react';
import { LinkWithQuery } from '@/components/elements/LinkWithQuery';
import { FAQ } from '@/components/landing/landingTemplate/FAQ';
import { useTranslationContext } from '@/context/translation';
import {
	useLandingShownEvent,
	useSendToAmplitudeScrolledPercent,
	useTranslationWithLocale,
} from '@/hooks';
import { GoogleService } from '@/services';
import { buildLocaleHref } from '@/helpers/buildLocaleHref';
import { type LandingMain } from '@/types';
import {
	AMPLITUDE_EVENTS,
	DEFAULT_LOCALE,
	GOOGLE_EVENTS,
	LANDING_AMPLITUDE_EVENTS,
	LOCAL_STORAGE_KEYS,
} from '@/constants';
import { GeneralAggregateSchema } from './GeneralAggregateSchema';
import { MainAbout } from './main/components/MainAbout';
import { MainBenefits } from './main/components/MainBenefits';
import { MainCta } from './main/components/MainCta';
import { MainHero } from './main/components/MainHero';
import { MainTestimonials } from './main/components/MainTestimonials';
import { lato } from './main/fonts';
import { schemaObject_Main } from './schemaObject_Main';
import styles from './MainLanding.module.scss';

interface MainLandingProps {
	data: LandingMain;
}
const MainLanding: React.FC<MainLandingProps> = ({ data }) => {
	const { locale, translations } = useTranslationContext();
	const { t } = useTranslationWithLocale(
		data.translationLocale,
		locale,
		translations?.[data.translationLocale] as
			| Record<string, unknown>
			| undefined,
	);

	const currentLocale = locale || DEFAULT_LOCALE;
	const localeHref = React.useCallback(
		(path: string) => buildLocaleHref(path, currentLocale),
		[currentLocale],
	);
	const toolsHref = localeHref(data.toolsHref);

	const sendGoogleAnalytics = () => {
		GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);
	};
	useSendToAmplitudeScrolledPercent(
		[50, 75, 100],
		LOCAL_STORAGE_KEYS.LANDING_SCROLLED_POINTS,
		AMPLITUDE_EVENTS.WEBAPP_LANDING_SCROLLED,
		{ type: LANDING_AMPLITUDE_EVENTS.MAIN },
	);

	useLandingShownEvent({ type: LANDING_AMPLITUDE_EVENTS.MAIN });

	// Links sit inside the clickable FAQ item — following one mustn't toggle it
	const stopToggle = (event: React.MouseEvent) => event.stopPropagation();
	const faqLinks = {
		'{tools}': (
			<LinkWithQuery
				href={toolsHref}
				className={styles['landing__faq-link']}
				onClick={stopToggle}
			>
				{t('faqData_subtitle1Tools')}
			</LinkWithQuery>
		),
		'{pricing}': (
			<LinkWithQuery
				href={localeHref('/pricing')}
				className={styles['landing__faq-link']}
				onClick={stopToggle}
			>
				{t('faqData_subtitle2Pricing')}
			</LinkWithQuery>
		),
		'{privacy}': (
			<LinkWithQuery
				href={localeHref('/privacy-policy')}
				className={styles['landing__faq-link']}
				onClick={stopToggle}
			>
				{t('faqData_subtitle3Privacy')}
			</LinkWithQuery>
		),
	};

	return (
		<div className={`${styles['landing']} ${lato.variable}`}>
			<GeneralAggregateSchema schema={schemaObject_Main} />

			<MainHero
				data={data.hero}
				t={t}
				locale={currentLocale}
				toolsHref={toolsHref}
				onCtaClick={sendGoogleAnalytics}
			/>
			<MainAbout
				data={data.about}
				t={t}
				localeHref={localeHref}
				onExploreClick={sendGoogleAnalytics}
			/>
			<MainBenefits
				data={data.benefits}
				t={t}
				toolsHref={toolsHref}
				onCtaClick={sendGoogleAnalytics}
			/>
			<MainTestimonials data={data.testimonials} t={t} locale={currentLocale} />
			<FAQ
				data={data.faq}
				t={t}
				links={faqLinks}
				onClick={sendGoogleAnalytics}
				variant='main'
			/>
			<MainCta
				data={data.cta}
				t={t}
				toolsHref={toolsHref}
				onCtaClick={sendGoogleAnalytics}
			/>
		</div>
	);
};

export default MainLanding;
